import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export default function PoliciesPage() {
  const [openSection, setOpenSection] = useState('terms');

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? '' : section);
  };

  return (
    <div className="min-h-screen bg-[#FDF6E8] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-8 text-center">
          Policies & <span className="text-blue-600">Information</span>
        </h1>

        {/* Terms and Conditions */}
        <div className="mb-4">
          <button
            onClick={() => toggleSection('terms')}
            className="w-full card-plain flex items-center justify-between hover:border-blue-500 transition-colors"
            data-testid="toggle-terms"
          >
            <h2 className="font-heading text-2xl font-bold text-slate-900">Terms and Conditions</h2>
            {openSection === 'terms' ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </button>
          {openSection === 'terms' && (
            <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6 -mt-2 font-body text-slate-700 space-y-6">
              <p className="text-sm text-slate-500">Artsy Craftsy After School Art Club • Last updated: March 2026</p>
              
              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">1. About Artsy Craftsy</h3>
                <p>Artsy Craftsy provides after-school art classes and occasional holiday workshops for children in Years 1–6. Our aim is to encourage creativity, artistic development, and confidence in a supportive environment.</p>
                <p className="mt-2">By registering your child for a class or workshop, you agree to the terms outlined below.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">2. Bookings and Payments</h3>
                <p>Bookings must be made through the Artsy Craftsy website and payment is processed securely online.</p>
                <div className="mt-3 bg-blue-50 p-4 rounded-xl">
                  <p className="font-semibold mb-2">Course fees:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>£8 per child per session</li>
                    <li>£96 for a 12-week term</li>
                    <li>Sibling discount: £7 per session</li>
                    <li>Returning student discount: £62 per term</li>
                  </ul>
                </div>
                <p className="mt-3">A place in the class is only secured once payment has been completed.</p>
                <p className="mt-2">Each class has a maximum capacity of 20 children.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">3. Starter Art Pack</h3>
                <p>Each child enrolling in a full course receives a starter art pack included within the course fee.</p>
                <p className="mt-2">The pack contains the materials required for the projects completed throughout the term.</p>
                <p className="mt-2">Children joining part way through the term may not receive a full starter pack, as some materials may already have been used in earlier sessions.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">4. Delivery of Materials</h3>
                <p>Art materials are not delivered to homes.</p>
                <p className="mt-2">All materials included in the starter pack will be given directly to children on the first day they attend the art club.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">5. Refund Policy</h3>
                <p>Due to the limited number of places and preparation of materials, the following refund policy applies:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>No refunds will be issued within 7 days of the course start date, unless the place is filled by another child.</li>
                  <li>No refunds will be issued once the course has started, unless the place is taken by another child.</li>
                  <li>If a replacement child takes the place, a refund may be issued.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">6. Materials Policy</h3>
                <p>Starter pack materials are provided for use throughout the course.</p>
                <p className="mt-2">Materials cannot be returned once the course has started.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">7. Behaviour</h3>
                <p>Children are expected to behave respectfully towards the tutor and other students. Artsy Craftsy reserves the right to remove a child from a session if behaviour is unsafe or disruptive.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">8. Changes to Classes</h3>
                <p>Artsy Craftsy reserves the right to make changes to class schedules if necessary. Where possible, parents will be notified in advance.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">9. Liability</h3>
                <p>While every effort is made to ensure a safe environment, Artsy Craftsy cannot be held responsible for loss or damage to personal belongings.</p>
                <p className="mt-2">Parents must ensure that all medical and emergency contact information provided during registration is accurate.</p>
              </section>
            </div>
          )}
        </div>

        {/* Privacy Policy */}
        <div className="mb-4">
          <button
            onClick={() => toggleSection('privacy')}
            className="w-full card-plain flex items-center justify-between hover:border-pink-500 transition-colors"
            data-testid="toggle-privacy"
          >
            <h2 className="font-heading text-2xl font-bold text-slate-900">Privacy Policy</h2>
            {openSection === 'privacy' ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </button>
          {openSection === 'privacy' && (
            <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6 -mt-2 font-body text-slate-700 space-y-6">
              <p className="text-sm text-slate-500">Last updated: March 2026</p>
              
              <p>Artsy Craftsy is committed to protecting your privacy and handling your personal information responsibly in accordance with UK data protection laws.</p>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Information We Collect</h3>
                <p>When registering your child, we may collect:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Parent or guardian name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Child's name and age</li>
                  <li>School class and year group</li>
                  <li>Medical information or allergies</li>
                  <li>Emergency contact details</li>
                  <li>Photo permission preferences</li>
                </ul>
                <p className="mt-3">Payment information is processed securely through our payment provider and is not stored on our servers.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">How We Use Your Information</h3>
                <p>We use the information collected to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Manage bookings for the art club</li>
                  <li>Communicate with parents about classes and workshops</li>
                  <li>Send weekly summaries and updates</li>
                  <li>Ensure children's safety during sessions</li>
                  <li>Maintain accurate administrative records</li>
                </ul>
                <p className="mt-3 font-semibold">We do not sell or share your personal data with third parties for marketing purposes.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Data Storage</h3>
                <p>Your information is stored securely and only accessible to authorised administrators. Data is kept only for as long as necessary to provide services and meet legal obligations.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Your Rights</h3>
                <p>You have the right to request access to the personal information we hold about you and request corrections if necessary.</p>
              </section>
            </div>
          )}
        </div>

        {/* Cookie Policy */}
        <div className="mb-4">
          <button
            onClick={() => toggleSection('cookies')}
            className="w-full card-plain flex items-center justify-between hover:border-yellow-500 transition-colors"
            data-testid="toggle-cookies"
          >
            <h2 className="font-heading text-2xl font-bold text-slate-900">Cookie Policy</h2>
            {openSection === 'cookies' ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </button>
          {openSection === 'cookies' && (
            <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6 -mt-2 font-body text-slate-700 space-y-6">
              <p className="text-sm text-slate-500">Last updated: March 2026</p>
              
              <p>The Artsy Craftsy website uses cookies to ensure the website functions properly and to improve user experience.</p>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">What Are Cookies</h3>
                <p>Cookies are small text files stored on your device when you visit a website.</p>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">How We Use Cookies</h3>
                <p>We use cookies to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Enable essential website functionality such as bookings and login</li>
                  <li>Support secure payment processing</li>
                  <li>Understand how visitors use the website so we can improve it</li>
                </ul>
              </section>

              <section>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Managing Cookies</h3>
                <p>When you visit our website, you will be presented with a cookie banner allowing you to accept or manage cookies.</p>
                <p className="mt-2">You can also control cookies through your web browser settings.</p>
              </section>
            </div>
          )}
        </div>

        {/* Materials Delivery */}
        <div className="mb-4">
          <button
            onClick={() => toggleSection('materials')}
            className="w-full card-plain flex items-center justify-between hover:border-green-500 transition-colors"
            data-testid="toggle-materials"
          >
            <h2 className="font-heading text-2xl font-bold text-slate-900">Materials Delivery Information</h2>
            {openSection === 'materials' ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </button>
          {openSection === 'materials' && (
            <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6 -mt-2 font-body text-slate-700 space-y-4">
              <p>All starter art pack materials are distributed in person at the first session of the art club.</p>
              <p>Materials are prepared in advance and allocated to each child attending the course.</p>
              
              <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                <p className="font-semibold mb-2">For this reason:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Materials are not posted or delivered to homes</li>
                  <li>Materials cannot be returned once the course has begun</li>
                </ul>
              </div>
              
              <p>Children who join the course after the first session may receive a reduced set of materials depending on availability, but will still be provided with the items required to participate in the remaining sessions.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
